import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Store } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const pub = (p) => (p ? `${window.location.origin}/api/public/img/${p}` : null);
const EMPTY = { name: "", sector: "", text: "", quote: "", quote_by: "", since: "", email: "", order: "" };

export default function AdminPartners() {
  const [partners, setPartners] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/public/content")
      .then((r) => setPartners(r.data.partners))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      if (!editId) fd.append("name", form.name);
      Object.entries(form).forEach(([k, v]) => {
        if ((editId || k !== "name") && String(v ?? "").trim()) fd.append(k, v);
      });
      if (fileRef.current?.files?.[0]) fd.append("file", fileRef.current.files[0]);
      if (editId) {
        await api.patch(`/admin/partners/${editId}`, fd);
        toast.success("Partenaire mis à jour.");
      } else {
        await api.post("/admin/partners", fd);
        toast.success("Partenaire ajouté : il apparaît maintenant sur le site.");
      }
      setForm(EMPTY);
      setEditId(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le partenaire « ${p.name} » du site ?`)) return;
    try {
      await api.delete(`/admin/partners/${p.id}`);
      toast.success("Partenaire retiré du site.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name || "", sector: p.sector || "", text: p.text || "",
      quote: p.quote || "", quote_by: p.quote_by || "", since: p.since || "",
      order: p.order ?? "",
    });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-8" data-testid="admin-partners-view">
      <section>
        <h2 className="font-display text-xl font-bold text-korban-navy">
          {editId ? "Modifier le partenaire" : "Ajouter un partenaire"}
        </h2>
        <form onSubmit={submit} data-testid="partner-form" className="mt-4 grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-2 sm:p-6">
          <div>
            <label htmlFor="p-name" className="mb-1 block text-xs font-bold text-slate-600">Nom *</label>
            <input id="p-name" required minLength={2} value={form.name} onChange={set("name")}
              placeholder="Ex. ALFORNO" data-testid="partner-input-name" className="field field-light" />
          </div>
          <div>
            <label htmlFor="p-sector" className="mb-1 block text-xs font-bold text-slate-600">Secteur d'activité</label>
            <input id="p-sector" value={form.sector} onChange={set("sector")}
              placeholder="Ex. Boulangerie · Pâtisserie · Glacier" data-testid="partner-input-sector" className="field field-light" />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="p-text" className="mb-1 block text-xs font-bold text-slate-600">Description (ce que vous faites pour lui)</label>
            <textarea id="p-text" rows={2} value={form.text} onChange={set("text")}
              placeholder="Ex. ALFORNO fait recours à KORBAN CONSULTING pour…" data-testid="partner-input-text" className="field field-light resize-none" />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="p-quote" className="mb-1 block text-xs font-bold text-slate-600">Citation du partenaire (facultatif)</label>
            <textarea id="p-quote" rows={2} value={form.quote} onChange={set("quote")}
              placeholder="Ex. Avec eux nous n'avons crainte d'aucun problème" data-testid="partner-input-quote" className="field field-light resize-none" />
          </div>
          <div>
            <label htmlFor="p-quoteby" className="mb-1 block text-xs font-bold text-slate-600">Signature de la citation</label>
            <input id="p-quoteby" value={form.quote_by} onChange={set("quote_by")}
              placeholder="Ex. ALFORNO" data-testid="partner-input-quoteby" className="field field-light" />
          </div>
          <div>
            <label htmlFor="p-email" className="mb-1 block text-xs font-bold text-slate-600">Email du partenaire (pour la newsletter)</label>
            <input id="p-email" type="email" value={form.email} onChange={set("email")}
              placeholder="Ex. contact@alforno.ci" data-testid="partner-input-email" className="field field-light" />
          </div>
          <div>
            <label htmlFor="p-since" className="mb-1 block text-xs font-bold text-slate-600">Badge (facultatif)</label>
            <input id="p-since" value={form.since} onChange={set("since")}
              placeholder="Ex. Partenaire depuis plus de 5 ans" data-testid="partner-input-since" className="field field-light" />
          </div>
          <div>
            <label htmlFor="p-order" className="mb-1 block text-xs font-bold text-slate-600">Ordre d'affichage</label>
            <input id="p-order" type="number" value={form.order} onChange={set("order")}
              placeholder="0" data-testid="partner-input-order" className="field field-light" />
          </div>
          <div>
            <label htmlFor="p-logo" className="mb-1 block text-xs font-bold text-slate-600">Logo (image)</label>
            <input id="p-logo" type="file" ref={fileRef} accept=".jpg,.jpeg,.png,.webp,.gif"
              data-testid="partner-input-logo"
              className="block w-full text-xs text-slate-600 file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-korban-navy file:px-4 file:py-2 file:text-xs file:font-bold file:text-korban-gold" />
          </div>
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <button type="submit" disabled={busy} data-testid="partner-submit-button"
              className="inline-flex items-center gap-2 rounded-lg bg-korban-navy px-6 py-3 text-sm font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              {editId ? "Enregistrer les modifications" : "Ajouter au site"}
            </button>
            {editId && (
              <button type="button" onClick={() => { setEditId(null); setForm(EMPTY); }} data-testid="partner-cancel-edit"
                className="rounded-lg bg-white px-5 py-3 text-sm font-bold text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50">
                Annuler
              </button>
            )}
          </div>
        </form>
      </section>

      <section data-testid="partners-list-section">
        <h2 className="flex items-center gap-2 font-display text-xl font-bold text-korban-navy">
          <Store size={18} className="text-korban-blue" /> Partenaires affichés sur le site
        </h2>
        {!partners ? (
          <p className="mt-3 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        ) : partners.length === 0 ? (
          <p className="mt-3 rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500" data-testid="partners-admin-empty">
            Aucun partenaire enregistré. Les partenaires par défaut du site restent affichés tant que la liste est vide.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {partners.map((p) => (
              <div key={p.id} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`partner-row-${p.id}`}>
                {p.logo_path ? (
                  <img src={pub(p.logo_path)} alt={p.name} className="h-14 w-14 shrink-0 rounded-xl object-cover" />
                ) : (
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-korban-navy2/10 font-display text-xl font-bold text-korban-navy2">
                    {(p.name || "?").slice(0, 1)}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold text-korban-navy">{p.name}</p>
                  <p className="truncate text-xs text-slate-500">{p.sector || "—"}{p.since ? ` · ${p.since}` : ""}</p>
                </div>
                <button onClick={() => startEdit(p)} data-testid={`partner-edit-${p.id}`}
                  className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-korban-navy2 ring-1 ring-slate-200 hover:bg-slate-50">
                  <Pencil size={13} />
                </button>
                <button onClick={() => remove(p)} data-testid={`partner-delete-${p.id}`}
                  className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-red-600 ring-1 ring-red-200 hover:bg-red-50">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
